import { computed, ref } from 'vue'
import { defineStore } from 'pinia';
import { useSessionStore } from "@/stores/session";
import * as amplitude from "@amplitude/analytics-browser";
import { useGtm } from "@gtm-support/vue-gtm";
import { useAppStore } from "@/stores/app";
import { compareTierAndPlan } from '@/utils/membershipUtility'

export type SubscriptionFlowStep = 'welcome' | 'tier' | 'card' | 'referral' | 'success' | 'error' | 'review';
export type SubscriptionFlowBillingStep = 'checkout' | 'card-select' | 'card-form';
export type SubscriptionFlowMode = 'subscribe' | 'card' | 'update' | 'upgrade';

// Shared app state
export const useSubscriptionFlowStore = defineStore('subscription-flow', () => {

  const showModal = ref<boolean>(false);
  const step = ref<SubscriptionFlowStep>('welcome');
  const error = ref<string>('');
  const canClose = ref<boolean>(true);

  const membershipTierId = ref<number | null>(null);
  const membershipTierPlanId = ref<number | null>(null);

  function init(_step: SubscriptionFlowStep = 'welcome', _mode: SubscriptionFlowMode = 'subscribe') {
    step.value = _step;
    mode.value = _mode;
    showModal.value = true;
  }

  // New Subscription Flow components only (moved into the store from the main component)
  const mode = ref<SubscriptionFlowMode>('upgrade');
  const billingStep = ref<SubscriptionFlowBillingStep>('checkout');
  const billingCardId = ref<number | null>(null);

  // If member has a tier assigned but no card (import), show the card step
  function checkMembershipModel() {
    const sessionStore = useSessionStore();
    if (sessionStore.mustActivate) {
      step.value = 'card';
    } else {
      step.value = 'tier';
    }
  }

  function advanceStep() {
    const appStore = useAppStore();

    // Check what the next step should be
    if (step.value === 'welcome') {
      checkMembershipModel();
    } else if (step.value === 'tier') {
      step.value = isUpgrade.value ? 'card' : 'review';
    } else if (step.value === 'card') {
      // reset billing step
      billingStep.value = 'checkout';
      billingCardId.value = null;

      if (appStore.isReferralEnabled) {
        step.value = 'referral';
      } else {
        step.value = 'success';
      }
    } else if (step.value === 'referral') {
      step.value = 'success';
    } else if (step.value === 'success') {
      showModal.value = false;
      step.value = 'welcome';
    } else if (step.value === 'error') {
      showModal.value = false;
      step.value = 'welcome';
    } else {
      console.error('invalid step');
    }
  }

  function reportError(_error: string) {
    step.value = 'error';
    error.value = _error;
  }

  function close() {
    const gtm = useGtm();
    if (!canClose.value) {
      return;
    }

    amplitude.track('Subscription Modal Closed', {
      'Step': step.value,
    });

    if (step.value === 'card' && gtm) {
      gtm.trackEvent({
        event: 'cardModalClosed',
      });
    }

    $reset();
  }

  function changeBillingStep(step: SubscriptionFlowBillingStep) {
    billingStep.value = step;
  }

  function selectCard(cardId: number | null = null) {
    billingCardId.value = cardId;
    billingStep.value = 'checkout';
  }

  const selectedTier = computed(() => {
    const membershipTierIdValue = membershipTierId.value;
    if (membershipTierIdValue !== null) {
      const appStore = useAppStore();
      return appStore.membershipTiers.find(t => t.id === membershipTierIdValue);
    }
    return null;
  });

  const selectedTierPlan = computed(() => {
    const membershipTierPlanIdValue = membershipTierPlanId.value;
    if (selectedTier.value !== null && membershipTierPlanIdValue !== null) {
      const tier = selectedTier.value;
      if (tier && tier.membershipTierPlans) {
        return tier.membershipTierPlans.find(tp => tp.id === membershipTierPlanIdValue);
      }
    }
    return null;
  });

  const isUpgrade = computed(() => {
    const sessionStore = useSessionStore();

    if (!sessionStore.currentTier || !selectedTier.value) {
      return false;
    }

    return compareTierAndPlan(sessionStore.currentTier, selectedTier.value, sessionStore.currentTierPlan, selectedTierPlan.value) === 1;
  });

  function $reset() {
    showModal.value = false;
    step.value = 'welcome';
    billingStep.value = 'checkout';
    membershipTierId.value = null;
    membershipTierPlanId.value = null;
    billingCardId.value = null;
  }

  return {
    showModal,
    mode,
    step,
    error,
    membershipTierId,
    membershipTierPlanId,
    selectedTier,
    selectedTierPlan,
    canClose,
    isUpgrade,
    init,
    checkMembershipModel,
    advanceStep,
    reportError,
    billingStep,
    billingCardId,
    changeBillingStep,
    selectCard,
    close,
    $reset,
  };
});
